import { ArrayToIterable } from "../async/ArrayToIterable"
import { UInt8ToString } from "../bytes/UInt8ToString"
import { UnixTime } from "../time/UnixTime"
import type { AsyncSequence } from "../types/AsyncSequence"
import type { Time } from "../types/Units"

const readArray = (array: Uint8Array) => {
  const view = new DataView(array.buffer, array.byteOffset)
  return {
    get32: (offset: number) => view.getUint32(offset, true),
    get16: (offset: number) => view.getUint16(offset, true),
  }
}

const readFromStream = async (
  size: number,
  stream: AsyncGenerator<Uint8Array>
): Promise<{ bytes: Uint8Array; stream: AsyncIterator<Uint8Array> }> => {
  const bytes = new Uint8Array(size)
  let offset = 0

  while (offset < size) {
    const { value, done } = await stream.next()
    if (done) {
      throw new Error(
        `Unexpected end of stream: expected ${size} bytes, got ${offset} bytes`
      )
    }

    const needed = size - offset
    if (value.length <= needed) {
      bytes.set(value, offset)
      offset += value.length
    } else {
      // Split the chunk: take what we need, return the rest
      bytes.set(value.slice(0, needed), offset)
      offset += needed

      // Create a new iterator that yields the remainder first

      return {
        bytes,
        stream: ArrayToIterable([value.slice(needed)], stream),
      }
    }
  }

  return { bytes, stream }
}

const createReader = (initial: AsyncSequence<Uint8Array>) => {
  let current: AsyncGenerator<Uint8Array> = ArrayToIterable(initial)

  return async (size: number): Promise<Uint8Array> => {
    const { stream, bytes } = await readFromStream(size, current)
    current = stream as AsyncGenerator<Uint8Array>
    return bytes
  }
}

const parse_dos_datetime = (dosTime: number, dosDate: number): Time => {
  const seconds = (dosTime & 0x1f) * 2
  const minutes = (dosTime >> 5) & 0x3f
  const hours = (dosTime >> 11) & 0x1f

  const day = dosDate & 0x1f
  const month = ((dosDate >> 5) & 0x0f) - 1
  const year = ((dosDate >> 9) & 0x7f) + 1980

  const date = new Date(Date.UTC(year, month, day, hours, minutes, seconds))
  return UnixTime(date)
}

/**
 * Extracts files from a ZIP archive stream.
 * Only supports STORE compression method (uncompressed files).
 * Reads local file headers and extracts file data with metadata.
 *
 * @param chunks - Async sequence of Uint8Array chunks representing the ZIP file
 * @returns AsyncGenerator yielding objects with name, blob, and modified time
 *
 * @example
 * ```ts
 * import { ZipExtract } from "@pdfking/tfunc"
 *
 * const response = await fetch("archive.zip")
 * const stream = StreamToIterable(response.body)
 *
 * for await (const file of ZipExtract(stream)) {
 *   console.log(`Extracting: ${file.name}`)
 *   console.log(`Modified: ${FromUnixTime(file.modified)}`)
 *   console.log(`Size: ${file.blob.size} bytes`)
 *
 *   // Convert blob to text if needed
 *   const text = await file.blob.text()
 * }
 * ```
 */
export const ZipExtract = async function* (
  chunks: AsyncSequence<Uint8Array>
): AsyncGenerator<{
  name: string
  stream: Uint8Array
  modified: Time
}> {
  const read = createReader(chunks)

  while (true) {
    // Read local file header signature
    const sigBytes = await read(4)

    const { get32: sigGet32 } = readArray(sigBytes)
    const signature = sigGet32(0)

    // Check if we've reached the central directory
    if (signature === 0x02014b50) {
      // CENTRAL_DIRECTORY_HEADER_SIGNATURE
      break
    }

    // Check for local file header
    if (signature !== 0x04034b50) {
      // LOCAL_FILE_HEADER_SIGNATURE
      throw new Error(`Invalid ZIP signature: 0x${signature.toString(16)}`)
    }

    // Read rest of local file header (26 bytes after signature)

    const { get32, get16 } = await read(26).then(readArray)

    const compressionMethod = get16(4)
    const dosTime = get16(6)
    const dosDate = get16(8)
    const compressedSize = get32(14)
    const filenameLength = get16(22)
    const extraFieldLength = get16(24)

    // Only support uncompressed (store) method
    if (compressionMethod !== 0) {
      throw new Error(
        `Unsupported compression method: ${compressionMethod}. Only store (0) is supported.`
      )
    }

    // Read filename

    const name = await read(filenameLength).then(UInt8ToString)
    const modified = parse_dos_datetime(dosTime, dosDate)

    // Skip extra field by reading and discarding
    await read(extraFieldLength)

    // Read the file data - size is now in the header (no more data descriptor)
    yield {
      name,
      stream: await read(compressedSize),
      modified,
    }
  }
}
